import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Plane, PlaneTakeoff, Car, Hotel, Armchair, CircleParking, Sparkles,
  Newspaper, CloudSun, Radio, TicketPercent, Briefcase, Globe2, ShieldCheck,
  Headphones, Users, TowerControl, ChevronRight, Star, BookOpen, Camera, TrainFront,
} from "lucide-react";
import api from "../lib/api";

/* IndianAirports.com homepage — built from the user's mockup.
   Real data only: airports/states from the platform API, news from TP news API,
   weather live from Open-Meteo. Deals & traveler reports show honest
   "launching soon" states instead of fabricated content. */

const POPULAR = ["DEL", "BOM", "BLR", "COK", "MAA"];
const CHIPS = ["DEL", "BOM", "BLR", "COK", "MAA", "HYD", "CCU", "GOI"];
const WEATHER_COORDS = {
  DEL: [28.5562, 77.1], BOM: [19.0896, 72.8656], BLR: [13.1986, 77.7066],
  COK: [10.152, 76.4019], MAA: [12.9941, 80.1709],
};
const WMO = (c) => c === 0 ? "Clear" : c < 4 ? "Cloudy" : c < 50 ? "Fog" : c < 70 ? "Rain" : c < 80 ? "Snow" : c < 95 ? "Showers" : "Storms";

/* Native Hindi masthead labels when the traveler has switched language
   (googtrans cookie — language switch always reloads the page). */
const IS_HI = typeof document !== "undefined" && /googtrans=\/en\/hi/.test(document.cookie);
const T = (en, hi) => (IS_HI ? hi : en);
/* Devanagari breaks visually with uppercase/wide tracking — neutralize for Hindi */
const HT = IS_HI ? "notranslate normal-case tracking-normal" : "";

export default function IndiaAirportsHome() {
  const [data, setData] = useState(null);
  const [states, setStates] = useState([]);
  const [news, setNews] = useState([]);
  const [weather, setWeather] = useState({});
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "IndianAirports.com — India Begins at the Airport";
    api.get("/india/airports").then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
    api.get("/india/airports/states").then((r) => setStates(r.data.states)).catch(() => {});
    api.get("/news", { params: { category: "travel", limit: 4, q: "India airport aviation" } })
      .then((r) => setNews((r.data.items || r.data || []).slice(0, 4))).catch(() => {});
    Object.entries(WEATHER_COORDS).forEach(([iata, [lat, lon]]) => {
      fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const c = d?.current;
          if (!c || typeof c.temperature_2m !== "number") return; // rate-limited / bad payload
          setWeather((w) => ({ ...w, [iata]: { t: Math.round(c.temperature_2m), c: c.weather_code } }));
        })
        .catch(() => {});
    });
  }, []);

  const popular = useMemo(() => {
    const by = Object.fromEntries((data?.items || []).map((a) => [a.iata, a]));
    return POPULAR.map((i) => by[i]).filter(Boolean);
  }, [data]);
  const topStates = useMemo(() => [...states].sort((a, b) => b.airports - a.airports).slice(0, 6), [states]);

  const search = (e) => { e.preventDefault(); navigate(`/india/airports/directory${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); };

  return (
    <div data-testid="ia-home">
      {/* HERO — TravelerPulse.pro-style masthead, IndianAirports shades */}
      <div className="relative" data-testid="ia-hero">
        <img src="https://images.unsplash.com/photo-1507812984078-917a274065be?auto=format&fit=crop&w=2000&q=80" alt="Indian airport terminal" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/50 to-white/95" />
        <div className="relative max-w-6xl mx-auto px-5 pt-10 pb-8 text-center">
          {/* Brand lockup */}
          <div className="inline-flex items-center gap-2 sm:gap-4">
            <span className="inline-flex p-2 sm:p-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-800 via-blue-700 to-sky-600 text-white shadow-xl shrink-0"><TowerControl className="w-5 h-5 sm:w-9 sm:h-9" /></span>
            <h1 className="font-heading font-900 text-[27px] sm:text-5xl lg:text-6xl leading-none tracking-tight" data-testid="ia-home-title">
              <span className="text-blue-800">Indian</span><span className="text-orange-500">Airports</span><span className="text-zinc-500 text-lg sm:text-3xl lg:text-4xl">.com</span><span className="text-zinc-400 text-[10px] sm:text-base align-super">™</span>
            </h1>
          </div>
          <div className="mt-3 flex items-center justify-center gap-3" data-testid="ia-home-tagline">
            <span className="h-px w-8 sm:w-24 bg-zinc-500/60" />
            <p className={`text-[11px] sm:text-sm font-800 text-zinc-800 ${IS_HI ? "notranslate tracking-normal" : "uppercase tracking-[0.22em]"}`}>{T("India Begins at the Airport", "भारत की यात्रा एयरपोर्ट से शुरू होती है")}</p>
            <span className="h-px w-8 sm:w-24 bg-zinc-500/60" />
          </div>

          {/* Search */}
          <form onSubmit={search} className="mt-6 max-w-2xl mx-auto flex items-center bg-white rounded-2xl shadow-lg border border-zinc-200 p-1.5" data-testid="ia-home-search-form">
            <Search className="w-4 h-4 text-zinc-400 ml-3 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ia-home-search-input"
              placeholder="Search airport, city, IATA or state…"
              className="flex-1 px-3 py-2.5 text-sm focus:outline-none min-w-0" />
            <button type="submit" data-testid="ia-home-search-btn" className="bg-blue-700 text-white text-sm font-800 rounded-xl px-6 py-2.5 hover:bg-blue-800">SEARCH</button>
          </form>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5" data-testid="ia-home-chips">
            <span className="text-[10px] font-800 uppercase tracking-wide text-zinc-600">Popular:</span>
            {CHIPS.map((c) => (
              <Link key={c} to={`/india/airports/${c}`} data-testid={`ia-home-chip-${c}`}
                className="bg-white/90 border border-zinc-300 rounded-lg px-2.5 py-1 text-xs font-mono font-800 text-zinc-700 hover:border-blue-500 hover:text-blue-700">{c}</Link>
            ))}
            <Link to="/flight-search" className="bg-zinc-900/85 text-white rounded-lg px-2.5 py-1 text-xs font-700 inline-flex items-center gap-1 hover:bg-zinc-900" data-testid="ia-home-chip-flightstatus"><Plane className="w-3 h-3" /> Flight Status</Link>
          </div>

          {/* Tile cards — different shades per tile; 2-row grid on phones */}
          <div className="mt-8 grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:items-stretch sm:justify-center sm:gap-2.5" data-testid="ia-home-quick-actions">
            <HeroTile icon={TowerControl} label={T("Airports", "हवाई अड्डे")} color="text-blue-700" to="/india/airports/directory" testid="qa-find" />
            <HeroTile icon={Plane} label={T("Airlines", "एयरलाइंस")} color="text-sky-600" to="/india/airlines" testid="qa-airlines" />
            <HeroTile icon={PlaneTakeoff} label={T("My Flight", "मेरी उड़ान")} color="text-indigo-600" to="/india/my-flight" testid="qa-myflight" />
            <HeroTile icon={Radio} label={T("Flights", "उड़ानें")} color="text-orange-500" to="/flight-search" testid="qa-flights" />
            <HeroTile icon={Star} label={T("Reviews", "समीक्षाएं")} color="text-rose-600" to="/india/airports/directory" testid="qa-reviews" />
            <HeroTile icon={BookOpen} label={T("Guides", "गाइड")} color="text-emerald-600" to="/india/guides" testid="qa-guides" />
            <HeroTile icon={Newspaper} label={T("News", "समाचार")} color="text-violet-600" to="/travel-news" testid="qa-news" />
            <Link to="/travel-hub" data-testid="ia-home-qa-hub" className="bg-white/95 backdrop-blur border border-zinc-200 rounded-2xl shadow-md px-2 py-3 sm:px-5 sm:py-4 text-center flex flex-col items-center justify-center gap-1.5 hover:-translate-y-0.5 hover:shadow-lg transition-transform w-full sm:w-[136px]">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-700" />
              <span className={`text-[9px] sm:text-[11px] font-900 uppercase tracking-wide text-zinc-800 leading-tight ${HT}`}>{T("My Travel Hub", "मेरा ट्रैवल हब")}</span>
              <span className={`bg-blue-700 text-white text-[8px] sm:text-[10px] font-800 uppercase rounded-full px-2 sm:px-3 py-0.5 sm:py-1 ${HT}`}>{T("Join Our Hub", "हब से जुड़ें")}</span>
            </Link>
            <div className="col-span-4 sm:col-auto order-last sm:order-none flex flex-col gap-2">
              <HeroMini icons={[Car, TrainFront]} label={T("Taxi & Metro", "टैक्सी और मेट्रो")} color="text-rose-700" to="/ground" testid="qa-transport" />
              <HeroMini icons={[Hotel]} label={T("Hotels & Stay", "होटल और ठहराव")} color="text-teal-700" to="/stays" testid="qa-hotels" />
              <HeroMini icons={[Armchair, CircleParking]} label={T("Lounges & Parking", "लाउंज और पार्किंग")} color="text-amber-700" to="/india/airports/directory" testid="qa-lounges" />
            </div>
          </div>
        </div>
      </div>

      {/* WIDE PILL SHORTCUTS */}
      <div className="max-w-6xl mx-auto px-5 -mt-4 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="ia-home-pills">
          <HeroPill icon={Star} color="text-amber-500" label="Reviews & Ratings" to="/community" testid="pill-reviews" />
          <HeroPill icon={Camera} color="text-fuchsia-600" label="Photos & Stories" to="/community" testid="pill-photos" />
          <HeroPill icon={TicketPercent} color="text-orange-600" label="Deals & Offers" href="#ia-deals" testid="pill-deals" />
          <HeroPill icon={Sparkles} color="text-violet-600" label="Ask Airport AI" onClick={() => window.dispatchEvent(new Event("tp:ask-open"))} testid="qa-ai" />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] font-800 uppercase tracking-wide text-zinc-600" data-testid="ia-home-strip">
          <span>Explore. Fly. Share. Travel.</span>
          <span>Every Airport. Every Journey. (IndianAirports.com™)</span>
        </div>
      </div>

      {/* 3-COLUMN BAND: popular / states / news */}
      <div className="max-w-6xl mx-auto px-5 mt-8 grid lg:grid-cols-3 gap-5">
        <section className="bg-white border border-zinc-200 rounded-2xl p-4 min-w-0" data-testid="ia-home-popular">
          <SectionHead title="Popular Airports" to="/india/airports/directory" />
          <div className="mt-2 space-y-1.5">
            {popular.map((a) => (
              <Link key={a.iata} to={`/india/airports/${a.slug}`} data-testid={`ia-home-popular-${a.iata}`}
                className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-blue-50 group min-w-0">
                <img src={a.image_url} alt="" className="w-14 h-10 rounded-lg object-cover shrink-0" loading="lazy" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-700 truncate group-hover:text-blue-700">{a.name}</span>
                  <span className="block text-[11px] text-zinc-500"><b className="font-mono">{a.iata}</b> · {a.city}, {a.state}</span>
                </span>
                {a.type === "international" && <span className="ml-auto text-[9px] font-800 uppercase bg-sky-50 text-sky-700 border border-sky-200 rounded px-1.5 py-0.5 shrink-0">Intl</span>}
              </Link>
            ))}
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="ia-home-states">
          <SectionHead title="Explore India by State" to="/india/airports/directory" label="All states" />
          <div className="mt-2 space-y-1">
            {topStates.map((s) => (
              <button key={s.state} onClick={() => navigate(`/india/airports/states/${s.slug}`)} data-testid={`ia-home-state-${s.state.replace(/\s+/g, "-")}`}
                className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-orange-50 group">
                <Globe2 className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="text-sm font-700 group-hover:text-orange-700">{s.state}</span>
                <span className="ml-auto text-xs text-zinc-400">{s.airports} airport{s.airports > 1 ? "s" : ""}</span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-zinc-400 px-2">{states.length} states & UTs covered — growing toward every operational airport in India.</p>
        </section>

        <section className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="ia-home-news">
          <SectionHead title="Aviation & Travel News" to="/travel-news" />
          <div className="mt-2 space-y-2">
            {news.length === 0 ? (
              <p className="text-xs text-zinc-400 px-2 py-6 text-center">Latest aviation headlines load here.</p>
            ) : news.map((n, i) => (
              <a key={i} href={n.link || n.url} target="_blank" rel="noreferrer" className="block rounded-xl px-2.5 py-2 hover:bg-zinc-50" data-testid={`ia-home-news-${i}`}>
                <span className="text-sm font-600 leading-snug line-clamp-2">{n.title}</span>
                <span className="block text-[11px] text-zinc-400 mt-0.5 inline-flex items-center gap-1"><Newspaper className="w-3 h-3" /> {n.source || "News"}</span>
              </a>
            ))}
          </div>
        </section>
      </div>

      {/* SECOND BAND: weather / deals / reports+journey */}
      <div className="max-w-6xl mx-auto px-5 mt-5 grid lg:grid-cols-3 gap-5">
        <section className="bg-gradient-to-br from-sky-50 to-white border border-sky-200 rounded-2xl p-4" data-testid="ia-home-weather">
          <h2 className="font-heading font-800 text-base md:text-lg flex items-center gap-2"><CloudSun className="w-4 h-4 text-sky-600" /> Weather at Major Airports</h2>
          <div className="mt-3 grid grid-cols-5 gap-1.5">
            {POPULAR.map((iata) => (
              <Link key={iata} to={`/india/airports/${iata}`} className="bg-white border border-sky-100 rounded-xl px-1.5 py-2.5 text-center hover:border-sky-400" data-testid={`ia-home-weather-${iata}`}>
                <div className="text-xs font-mono font-800">{iata}</div>
                <div className="text-sm font-800 text-sky-800 mt-1">{weather[iata] ? `${weather[iata].t}°C` : "…"}</div>
                <div className="text-[10px] text-zinc-500">{weather[iata] ? WMO(weather[iata].c) : ""}</div>
              </Link>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-zinc-400">Live from Open-Meteo. Weather alone doesn't confirm delays — check your airline.</p>
        </section>

        <section id="ia-deals" className="bg-white border border-zinc-200 rounded-2xl p-4 scroll-mt-24" data-testid="ia-home-deals">
          <h2 className="font-heading font-800 text-base md:text-lg flex items-center gap-2"><TicketPercent className="w-4 h-4 text-orange-600" /> Top Deals for You</h2>
          <div className="mt-3 space-y-2">
            {[["Airport taxis & transfers", Car], ["Airport parking", CircleParking], ["Lounge access", Armchair], ["Airport hotels", Hotel]].map(([label, Icon]) => (
              <div key={label} className="flex items-center gap-3 bg-zinc-50 border border-zinc-100 rounded-xl px-3 py-2.5">
                <Icon className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="text-sm font-600">{label}</span>
                <span className="ml-auto text-[10px] font-800 uppercase bg-orange-50 text-orange-600 border border-orange-200 rounded-full px-2 py-0.5">Launching soon</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-zinc-400">Partner deals are curated and clearly marked — never pay-to-rank.</p>
        </section>

        <div className="space-y-5">
          <section className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="ia-home-reports">
            <h2 className="font-heading font-800 text-base md:text-lg flex items-center gap-2"><Radio className="w-4 h-4 text-emerald-600" /> Traveler Reports</h2>
            <p className="mt-1.5 text-xs text-zinc-600">Live queue times, baggage delays and airport tips from travelers — <b>Airport Pulse launches in the community phase</b>. Traveler reports will always be clearly separated from official airport information.</p>
          </section>
          <section className="bg-blue-700 text-white rounded-2xl p-4" data-testid="ia-home-journey">
            <h2 className="font-heading font-800 text-base md:text-lg flex items-center gap-2"><Briefcase className="w-4 h-4" /> Plan Your Journey</h2>
            <p className="mt-1 text-xs text-blue-100">Add flights, airports, hotels and transport to your travel hub — TravelerPulse organizes your travel day.</p>
            <Link to="/travel-hub" data-testid="ia-home-mth-cta" className="mt-3 inline-flex items-center gap-1.5 bg-white text-blue-800 text-xs font-800 rounded-full px-4 py-2 hover:bg-blue-50">GO TO MY TRAVEL HUB <ChevronRight className="w-3.5 h-3.5" /></Link>
          </section>
        </div>
      </div>

      {/* TRUST BAR */}
      <div className="max-w-6xl mx-auto px-5 mt-8 mb-2">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-center" data-testid="ia-home-trustbar">
          <Trust icon={TowerControl} a={`${data?.total || 44}+ Airports`} b="Across India" />
          <Trust icon={Plane} a="Flight Tools" b="Search & status" />
          <Trust icon={ShieldCheck} a="Trusted Information" b="Verified sources" />
          <Trust icon={Globe2} a="Multilingual" b="Translate any page" />
          <Trust icon={Headphones} a="Listen to Pages" b="Text to speech" />
          <Trust icon={Users} a="Community Driven" b="Share & help" />
        </div>
      </div>
    </div>
  );
}

function HeroTile({ icon: Icon, label, color, to, testid }) {
  return (
    <Link to={to} data-testid={`ia-home-${testid}`}
      className="bg-white/95 backdrop-blur border border-zinc-200 rounded-2xl shadow-md w-full sm:w-[104px] px-2 py-3 sm:px-3 sm:py-4 text-center hover:-translate-y-0.5 hover:shadow-lg transition-transform flex flex-col items-center justify-center gap-1.5 sm:gap-2">
      <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${color}`} />
      <span className={`block text-[9px] sm:text-[11px] font-900 uppercase tracking-wide text-zinc-800 leading-tight ${HT}`}>{label}</span>
    </Link>
  );
}

function HeroMini({ icons, label, color, to, testid }) {
  return (
    <Link to={to} data-testid={`ia-home-${testid}`}
      className="bg-white/95 backdrop-blur border border-zinc-200 rounded-xl shadow-md px-3.5 py-2 flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-lg transition-transform w-full sm:min-w-[168px] sm:flex-1">
      {icons.map((I, i) => <I key={i} className={`w-5 h-5 ${color}`} />)}
      <span className={`text-[10px] font-900 uppercase tracking-wide text-zinc-800 whitespace-nowrap ${HT}`}>{label}</span>
    </Link>
  );
}

function HeroPill({ icon: Icon, color, label, to, href, onClick, testid }) {
  const inner = (
    <>
      <Icon className={`w-5 h-5 ${color}`} />
      <span className="text-xs sm:text-sm font-800 uppercase tracking-wide text-zinc-800">{label}</span>
    </>
  );
  const cls = "bg-white border border-zinc-200 rounded-xl shadow-sm px-4 py-3 flex items-center justify-center gap-2 hover:border-blue-400 hover:shadow-md transition-shadow";
  if (to) return <Link to={to} className={cls} data-testid={`ia-home-${testid}`}>{inner}</Link>;
  if (href) return <a href={href} className={cls} data-testid={`ia-home-${testid}`}>{inner}</a>;
  return <button onClick={onClick} className={cls} data-testid={`ia-home-${testid}`}>{inner}</button>;
}

function SectionHead({ title, to, label = "View All" }) {
  return (
    <div className="flex items-center gap-2">
      <h2 className="font-heading font-800 text-base md:text-lg">{title}</h2>
      <Link to={to} className="ml-auto text-[11px] font-700 text-blue-700 hover:underline">{label}</Link>
    </div>
  );
}

function Trust({ icon: Icon, a, b }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl px-3 py-3.5">
      <Icon className="w-4 h-4 text-blue-700 mx-auto" />
      <div className="text-xs font-800 mt-1">{a}</div>
      <div className="text-[10px] text-zinc-400">{b}</div>
    </div>
  );
}
